/**
 * Leaflet + react-leaflet can call setLatLng with undefined when props.position / center
 * briefly desync (e.g. cluster spiderfy, RT updates). That poisons bound Popup/Tooltip
 * (_latlng undefined) and crashes in CRS.project / _updatePosition / _latLngToNewLayerPoint
 * (e.g. during zoomanim).
 * These guards no-op invalid updates so layers keep their last good position.
 */
import L from 'leaflet';

let installed = false;

function finiteNum(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

export function isSafeLeafletLatLngInput(latlng) {
  if (latlng == null) return false;
  if (Array.isArray(latlng)) {
    if (latlng.length < 2) return false;
    return Number.isFinite(finiteNum(latlng[0])) && Number.isFinite(finiteNum(latlng[1]));
  }
  if (typeof latlng === 'object') {
    const lat = finiteNum(latlng.lat);
    const lngRaw = latlng.lng !== undefined ? latlng.lng : latlng.lon;
    const lng = finiteNum(lngRaw);
    return Number.isFinite(lat) && Number.isFinite(lng);
  }
  return false;
}

/** True when a layer's internal _latlng is safe to pass to CRS / latLngToLayerPoint. */
function latLngReady(ll) {
  if (ll == null) return false;
  if (typeof ll.lat === 'number' && typeof ll.lng === 'number') {
    return Number.isFinite(ll.lat) && Number.isFinite(ll.lng);
  }
  return isSafeLeafletLatLngInput(ll);
}

export function installLeafletPositionGuards() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const origMarker = L.Marker.prototype.setLatLng;
  L.Marker.prototype.setLatLng = function guardedMarkerSetLatLng(latlng) {
    if (!isSafeLeafletLatLngInput(latlng)) return this;
    return origMarker.call(this, latlng);
  };

  const origMarkerUpdate = L.Marker.prototype.update;
  L.Marker.prototype.update = function guardedMarkerUpdate() {
    if (!this._icon || !this._map || !latLngReady(this._latlng)) return this;
    return origMarkerUpdate.call(this);
  };

  const origCircleMarker = L.CircleMarker.prototype.setLatLng;
  L.CircleMarker.prototype.setLatLng = function guardedCircleMarkerSetLatLng(latlng) {
    if (!isSafeLeafletLatLngInput(latlng)) return this;
    return origCircleMarker.call(this, latlng);
  };

  const origCircleMarkerProject = L.CircleMarker.prototype._project;
  L.CircleMarker.prototype._project = function guardedCircleMarkerProject() {
    if (!this._map || !latLngReady(this._latlng)) return;
    return origCircleMarkerProject.call(this);
  };

  const origDivOverlay =
    L.DivOverlay && L.DivOverlay.prototype && L.DivOverlay.prototype.setLatLng
      ? L.DivOverlay.prototype.setLatLng
      : null;
  if (origDivOverlay) {
    L.DivOverlay.prototype.setLatLng = function guardedDivOverlaySetLatLng(latlng) {
      if (!isSafeLeafletLatLngInput(latlng)) return this;
      return origDivOverlay.call(this, latlng);
    };
  }

  const origDivUpdatePos = L.DivOverlay.prototype._updatePosition;
  L.DivOverlay.prototype._updatePosition = function guardedDivOverlayUpdatePosition() {
    if (!this._map || !latLngReady(this._latlng)) return;
    return origDivUpdatePos.call(this);
  };

  if (L.Tooltip && L.Tooltip.prototype && L.Tooltip.prototype._updatePosition) {
    const origTipUpdatePos = L.Tooltip.prototype._updatePosition;
    L.Tooltip.prototype._updatePosition = function guardedTooltipUpdatePosition() {
      if (!this._map || !latLngReady(this._latlng)) return;
      return origTipUpdatePos.call(this);
    };
  }

  const origMarkerAnimateZoom = L.Marker.prototype._animateZoom;
  L.Marker.prototype._animateZoom = function guardedMarkerAnimateZoom(opt) {
    if (!this._map || !latLngReady(this._latlng) || !opt) return;
    return origMarkerAnimateZoom.call(this, opt);
  };

  const origDivAnimateZoom = L.DivOverlay.prototype._animateZoom;
  L.DivOverlay.prototype._animateZoom = function guardedDivOverlayAnimateZoom(e) {
    if (!this._map || !latLngReady(this._latlng) || !e) return;
    return origDivAnimateZoom.call(this, e);
  };

  if (L.Tooltip && L.Tooltip.prototype && L.Tooltip.prototype._animateZoom) {
    const origTooltipAnimateZoom = L.Tooltip.prototype._animateZoom;
    L.Tooltip.prototype._animateZoom = function guardedTooltipAnimateZoom(e) {
      if (!this._map || !latLngReady(this._latlng) || !e) return;
      return origTooltipAnimateZoom.call(this, e);
    };
  }
}

installLeafletPositionGuards();
